// ==================== ANALYTICS TRACKING SYSTEM ====================
// Handles Google Analytics 4 tracking and listing/ad counter increments

import { supabase, isSupabaseConfigured } from './supabase';
import { logger } from './logger';

// ==================== GA4 INTEGRATION ====================
let ga4Initialized = false;
let ga4MeasurementId = null;
let ga4InitPromise = null;
let pendingGA4Events = [];

export const initGA4 = (measurementId) => {
  if (!measurementId) return Promise.resolve(false);
  if (ga4Initialized) return Promise.resolve(true);
  if (ga4InitPromise) return ga4InitPromise;

  ga4InitPromise = new Promise((resolve) => {
    // Load gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;

    script.onload = () => {
      // Initialize gtag after script loads
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false // We'll manually track page views
      });

      ga4MeasurementId = measurementId;
      ga4Initialized = true;
      logger.log('GA4 initialized with:', measurementId);

      // Flush any pending events that were queued before initialization
      if (pendingGA4Events.length > 0) {
        logger.log(`Flushing ${pendingGA4Events.length} pending GA4 events`);
        pendingGA4Events.forEach(({ eventName, params }) => {
          window.gtag('event', eventName, params);
        });
        pendingGA4Events = [];
      }

      resolve(true);
    };

    script.onerror = () => {
      logger.error('Failed to load GA4 script');
      ga4InitPromise = null;
      resolve(false);
    };

    document.head.appendChild(script);
  });

  return ga4InitPromise;
};

// Check if GA4 is ready
export const isGA4Ready = () => ga4Initialized;

const trackGA4Event = (eventName, params = {}) => {
  // If GA4 is initialized, send immediately
  if (ga4Initialized && window.gtag) {
    window.gtag('event', eventName, params);
    return;
  }

  // If GA4 is being initialized, queue the event
  if (ga4InitPromise) {
    pendingGA4Events.push({ eventName, params });
    return;
  }

  // GA4 not configured, silently skip
};

// ==================== TRACKING FUNCTIONS ====================

// Track page views (GA4 only)
export const trackPageView = (pageName, pageTitle) => {
  trackGA4Event('page_view', {
    page_title: pageTitle || pageName,
    page_location: window.location.href,
    // The app now uses crawlable History API routes (for example /schools),
    // not the legacy hash routes. Reporting the actual path keeps GA4 page
    // reports aligned with Search Console and canonical URLs.
    page_path: `${window.location.pathname}${window.location.search}`
  });
};

// Track offence views (GA4 only)
export const trackOffenceView = (offenceCode, offenceTitle, category) => {
  trackGA4Event('view_item', {
    content_type: 'offence',
    item_id: offenceCode,
    item_name: offenceTitle,
    item_category: category
  });
};

// Track directory listing views (GA4 + increment counter)
export const trackDirectoryView = (type, id, name, parish) => {
  trackGA4Event('view_item', {
    content_type: type,
    item_id: id,
    item_name: name,
    item_category: type
  });

  // Increment listing view count for advertiser stats
  if (isSupabaseConfigured() && supabase) {
    supabase.rpc('increment_listing_view', { p_type: type, p_id: id }).catch(() => {});
  }
};

// Track directory listing clicks (GA4 + increment counter)
export const trackDirectoryClick = (type, id, name, clickType, parish) => {
  trackGA4Event('select_content', {
    content_type: type,
    item_id: id,
    item_name: name,
    click_type: clickType
  });

  // Increment listing click count for advertiser stats
  if (isSupabaseConfigured() && supabase) {
    supabase.rpc('increment_listing_click', { p_type: type, p_id: id }).catch(() => {});
  }
};

// Track ad impressions (GA4 + increment counter)
export const trackAdImpression = (adId, adName, placement, page) => {
  trackGA4Event('ad_impression', {
    ad_id: adId,
    ad_name: adName,
    ad_placement: placement,
    page_location: page
  });

  // Increment ad impression count
  if (isSupabaseConfigured() && supabase) {
    supabase.rpc('increment_ad_impression', { ad_id: adId }).catch(() => {});
  }
};

// Track ad clicks (GA4 + increment counter)
export const trackAdClick = (adId, adName, placement, page, targetUrl) => {
  trackGA4Event('ad_click', {
    ad_id: adId,
    ad_name: adName,
    ad_placement: placement,
    page_location: page,
    target_url: targetUrl
  });

  // Increment ad click count
  if (isSupabaseConfigured() && supabase) {
    supabase.rpc('increment_ad_click', { ad_id: adId }).catch(() => {});
  }
};

// Track searches (GA4 only)
export const trackSearch = (query, resultCount, page = 'offences') => {
  trackGA4Event('search', {
    search_term: query,
    search_results: resultCount
  });
};

// ==================== ANALYTICS API (for advertiser stats) ====================
export const analyticsAPI = {
  // Get advertiser's own listing stats from service_providers table
  getMyListingStats: async (userId) => {
    if (!isSupabaseConfigured()) return { data: null, error: 'Not configured' };

    const { data, error } = await supabase
      .from('service_providers')
      .select('id, name, type, view_count, click_count, tier')
      .eq('user_id', userId);

    if (error) return { data: [], error };

    const listings = (data || []).map(p => ({
      ...p,
      total_views: p.view_count || 0,
      total_clicks: p.click_count || 0
    }));

    return { data: listings, error: null };
  }
};

export default {
  initGA4,
  isGA4Ready,
  trackPageView,
  trackOffenceView,
  trackDirectoryView,
  trackDirectoryClick,
  trackAdImpression,
  trackAdClick,
  trackSearch,
  analyticsAPI
};
